'use client';

import { useEffect, useRef } from 'react';

import classnames from 'classnames';

import type { CheckboxProps } from './Checkbox.types';

import styles from './Checkbox.module.scss';

function Checkbox({
  className,
  style,
  checked = false,
  indeterminate = false,
  disabled = false,
  label,
  labelPosition = 'right',
  onChange,
  testId = 'ui-checkbox',
  ...props
}: CheckboxProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  // `indeterminate` is a DOM-only property (not an attribute), so it must be
  // set imperatively.
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <label
      style={style}
      className={classnames(
        styles.checkbox,
        !!label && styles[`checkbox--label-${labelPosition}`],
        disabled && styles['checkbox--disabled'],
        className,
      )}
    >
      <input
        {...props}
        ref={inputRef}
        type="checkbox"
        data-testid={testId}
        className={styles.checkbox__input}
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange?.(event.target.checked)}
      />
      <span
        aria-hidden="true"
        data-selected={checked || undefined}
        data-indeterminate={indeterminate || undefined}
        data-disabled={disabled || undefined}
        className={styles.checkbox__box}
      />
      {label && <span className={styles.checkbox__label}>{label}</span>}
    </label>
  );
}

export { Checkbox };
